import type { PlanWithLines } from 'app/types/treatment'
import { logError } from 'lib/logging'

type LinkedPlan = { id?: string | null; procedureIds?: string[] | null; procedureFeeOverrides?: string | null }
type LinkedProcedure = {
  id?: string | null
  planIds?: string[] | null
  feeAmount?: number | null
  state?: string | null
}

/**
 * A plan's own fee for each of its procedures, keyed by procedure id. The API
 * sends it as a JSON object string, e.g. `{"K-5fe8a7-9-54":375}`.
 */
export function parseFeeOverrides(raw: string | null | undefined): Map<string, number> {
  const overrides = new Map<string, number>()
  if (!raw) return overrides
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch (error) {
    logError(`Unreadable procedureFeeOverrides`, raw, error)
    return overrides
  }
  if (!parsed || typeof parsed !== `object` || Array.isArray(parsed)) return overrides
  for (const [id, fee] of Object.entries(parsed)) {
    if (typeof fee === `number` && Number.isFinite(fee)) overrides.set(id, fee)
  }
  return overrides
}

/**
 * Every plan a procedure sits on, read from both sides of the link. A
 * procedure's `planIds` keeps only one of the plans that list it (on dev, a
 * crown listed on plans 34 and 36 named only plan 4), so the plan's
 * `procedureIds` is the fuller side.
 */
export function planIdsFor(procedure: LinkedProcedure, plans: LinkedPlan[]): string[] {
  const ids = new Set(procedure.planIds ?? [])
  for (const plan of plans) {
    if (plan.id && procedure.id && (plan.procedureIds ?? []).includes(procedure.id)) ids.add(plan.id)
  }
  return [...ids]
}

const isDeleted = (procedure: LinkedProcedure) => (procedure.state ?? ``).toUpperCase() === `DELETED`

/**
 * Each plan with the procedure lines it carries, priced as the plan prices them.
 * Deleted lines stay visible at the end but count toward neither the total nor
 * the count.
 */
export function attachLines<Plan extends LinkedPlan, Procedure extends LinkedProcedure>(
  plans: Plan[],
  procedures: Procedure[],
): PlanWithLines<Plan, Procedure>[] {
  const byId = new Map(procedures.flatMap((procedure) => (procedure.id ? [[procedure.id, procedure] as const] : [])))

  return plans.map((plan) => {
    const listed = [...new Set(plan.procedureIds ?? [])].flatMap((id) => byId.get(id) ?? [])
    const named = procedures.filter(
      (procedure) => !!plan.id && (procedure.planIds ?? []).includes(plan.id) && !listed.includes(procedure),
    )
    const overrides = parseFeeOverrides(plan.procedureFeeOverrides)
    const lines = [...listed, ...named]
      .map((procedure) => ({
        procedure,
        fee: overrides.get(procedure.id ?? ``) ?? procedure.feeAmount ?? 0,
      }))
      .sort((a, b) => Number(isDeleted(a.procedure)) - Number(isDeleted(b.procedure)))
    const live = lines.filter((line) => !isDeleted(line.procedure))

    return {
      plan,
      lines,
      total: live.reduce((sum, line) => sum + line.fee, 0),
      count: live.length,
    }
  })
}
